// This code has been fully generated by an AI.
import { useEffect, useId, useState } from 'react';

interface HardDeleteVersionDialogProps {
  open: boolean;
  confirmationValue: string;
  busy?: boolean;
  error?: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export default function HardDeleteVersionDialog({
  open,
  confirmationValue,
  busy = false,
  error = '',
  onClose,
  onConfirm,
}: Readonly<HardDeleteVersionDialogProps>) {
  const id = useId();
  const [typedValue, setTypedValue] = useState('');
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!open) {
      setTypedValue('');
      setReason('');
      setAcknowledged(false);
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    globalThis.addEventListener('keydown', onKeyDown);
    return () => globalThis.removeEventListener('keydown', onKeyDown);
  }, [busy, onClose, open]);

  if (!open) return null;
  const canConfirm = acknowledged && typedValue.trim() === confirmationValue && reason.trim().length > 0;

  return (
    <div className="modal-backdrop" onClick={() => { if (!busy) onClose(); }}>
      <div
        className="modal modal--danger"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id={`${id}-title`} className="modal__title">Permanently purge version?</h2>
        <p id={`${id}-description`} className="modal__description">
          This physically removes the version, checksums, and associated version metrics. Existing pins and download URLs will stop working.
          A snapshot and the reason are retained in the purge audit log. This cannot be undone.
        </p>
        <div className="alert alert--info mb-2">Type <code>{confirmationValue}</code> to continue.</div>
        <div className="field">
          <label htmlFor={`${id}-confirmation`}>Version reference</label>
          <input
            id={`${id}-confirmation`}
            className="input"
            value={typedValue}
            onChange={(event) => setTypedValue(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            autoFocus
          />
        </div>
        <div className="field">
          <label htmlFor={`${id}-reason`}>Audit reason</label>
          <textarea
            id={`${id}-reason`}
            className="textarea"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={1000}
            required
          />
        </div>
        <label className="checkbox-row" htmlFor={`${id}-acknowledgement`}>
          <input
            id={`${id}-acknowledgement`}
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
          />
          <span>I understand this permanently removes the published artifact and breaks pinned installs.</span>
        </label>
        {error && <div className="alert alert--error mt-1" role="alert">{error}</div>}
        <div className="modal__actions">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="btn btn--danger"
            disabled={!canConfirm || busy}
            onClick={() => onConfirm(reason.trim())}
          >
            {busy ? 'Purging…' : 'Permanently purge'}
          </button>
        </div>
      </div>
    </div>
  );
}
